// Seller, visão geral (`spec/24-croqui-alinhamento.md` §5, WEB-51). Igual ao frame
// `screens/seller/visao-geral.tsx` do croqui em 1440.
//
// Herói = "Vendas no período". O saldo NÃO mora mais aqui (o croqui não tem bloco de
// saldo nesta tela): ele é `/app/balance` e o "Recebido no mês" da sidebar.
//
// Três leituras de `reports/summary`: a do filtro, a do período anterior (as comparações
// "+12 % vs. período anterior") e uma janela FIXA de 30 dias, que alimenta o aviso de
// chargeback e o KPI "Hoje", nenhum dos dois segue o filtro da tela. Quando o filtro já
// é 30 dias, o React Query serve a terceira do mesmo cache.
//
// `GET /v1/me/kyc` fica de fora de propósito: ela cria uma submissão `draft`
// (`spec/08` §4.2) e a tela abriria um rascunho de KYC a cada carregamento.

import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { apiKeysQueryOptions } from '../api/queries/api-keys.ts';
import {
  countersQueryOptions,
  last30Range,
  previousRange,
  profileQueryOptions,
  recentTransactionsQueryOptions,
  summaryQueryOptions,
} from '../api/queries/overview.ts';
import { endpointsQueryOptions } from '../api/queries/webhooks.ts';
import { API_BASE_URL } from '../app/config.ts';
import { useMode } from '../app/mode.tsx';
import { Btn } from '../components/kit/btn.tsx';
import { DateRangeFilter } from '../components/kit/date-range-filter.tsx';
import { PageHeader } from '../components/kit/page-header.tsx';
import { Button } from '../components/ui/button.tsx';
import { greeting, longDate } from '../features/overview/format.ts';
import {
  ChargebackNotice,
  ProfileErrorNotice,
  StatusNotice,
} from '../features/overview/notices.tsx';
import { HealthPanel, PendingPanel, VolumePanel } from '../features/overview/panels.tsx';
import { RecentTransactions } from '../features/overview/recent-transactions.tsx';
import { resolveRange, validateOverviewSearch } from '../features/overview/search.ts';
import { OverviewStats, OverviewStatsSkeleton } from '../features/overview/stats.tsx';
import { todayIsoDate } from '../lib/dates.ts';

function Overview() {
  const range = resolveRange(Route.useSearch());
  const navigateHere = Route.useNavigate();
  const navigate = useNavigate();
  const { mode } = useMode();
  const today = todayIsoDate();

  const summary = useQuery(summaryQueryOptions(mode, range));
  const previous = useQuery(summaryQueryOptions(mode, previousRange(range)));
  const last30 = useQuery(summaryQueryOptions(mode, last30Range(today)));
  const charges = useQuery(recentTransactionsQueryOptions(mode, today));
  const profile = useQuery(profileQueryOptions(mode));
  const counters = useQuery(countersQueryOptions(mode));
  const apiKeys = useQuery(apiKeysQueryOptions());
  const endpoints = useQuery(endpointsQueryOptions(mode));

  const go = (to: string): void => {
    void navigate({ to });
  };

  const todayGrossCents = last30.data?.daily.find((day) => day.date === today)?.gross_cents ?? 0;

  return (
    <>
      <PageHeader
        title={greeting(profile.data?.display_name)}
        sub={`${longDate()}${profile.data === undefined ? '' : ` · ${profile.data.store.name}`}`}
        path="/v1/me/reports/summary"
        actions={
          <>
            <DateRangeFilter
              value={range}
              onChange={(next) => {
                void navigateHere({ search: next, replace: true });
              }}
            />
            <Btn
              kind="card"
              icon="doc"
              onClick={() => {
                go('/app/reports');
              }}
            >
              Exportar
            </Btn>
            <Btn
              kind="primary"
              icon="arrow"
              onClick={() => {
                go('/app/withdrawals');
              }}
            >
              Sacar
            </Btn>
          </>
        }
      />

      {/* O croqui põe a saudação no lugar do título da página. O nome da TELA continua
          existindo para leitor de tela e para os testes que checam "cheguei no painel". */}
      <h2 className="sr-only">Visão geral</h2>

      {profile.isError ? (
        <ProfileErrorNotice
          error={profile.error}
          action={
            <Btn
              kind="card"
              small
              onClick={() => {
                void profile.refetch();
              }}
            >
              Tentar de novo
            </Btn>
          }
        />
      ) : (
        profile.data !== undefined && <StatusNotice status={profile.data.status} />
      )}

      {last30.data !== undefined && profile.data !== undefined && (
        <ChargebackNotice
          chargebacks={last30.data.chargebacks}
          autoApprove={profile.data.withdraw_auto_approve}
          action={
            <Btn
              kind="card"
              small
              onClick={() => {
                void navigate({ to: '/app/transactions', search: { status: 'chargeback' } });
              }}
            >
              Ver transações
            </Btn>
          }
        />
      )}

      <div className="grid items-stretch gap-4 lg:grid-cols-[1.3fr_1fr_1fr_1fr]">
        {summary.data === undefined ? (
          <OverviewStatsSkeleton />
        ) : (
          <OverviewStats
            summary={summary.data}
            previous={previous.data}
            todayGrossCents={todayGrossCents}
          />
        )}
      </div>

      <div className="grid min-h-0 flex-1 gap-5 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="flex min-w-0 flex-col gap-5">
          <VolumePanel
            summary={summary.data}
            isPending={summary.isPending}
            error={summary.error}
            onRetry={() => {
              void summary.refetch();
            }}
            range={range}
            today={today}
          />

          <RecentTransactions
            page={charges.data}
            isPending={charges.isPending}
            error={charges.error}
            onRetry={() => {
              void charges.refetch();
            }}
            onOpen={(id) => {
              go(`/app/transactions/${id}`);
            }}
            seeAll={
              <Btn
                kind="ghost"
                small
                onClick={() => {
                  go('/app/transactions');
                }}
              >
                Ver todas
              </Btn>
            }
            emptyAction={
              <Button asChild variant="outline" size="sm">
                <a href={`${API_BASE_URL}/v1/docs`} target="_blank" rel="noreferrer">
                  Ver a documentação da API
                </a>
              </Button>
            }
          />
        </div>

        <div className="flex flex-col gap-5">
          <PendingPanel
            counters={counters.data}
            kycStatus={profile.data?.kyc_status}
            isPending={counters.isPending}
            error={counters.error}
            onRetry={() => {
              void counters.refetch();
            }}
          />
          <HealthPanel
            counters={counters.data}
            apiKeys={apiKeys.data}
            endpoints={endpoints.data}
            isPending={counters.isPending && apiKeys.isPending && endpoints.isPending}
            error={apiKeys.error ?? endpoints.error}
            onRetry={() => {
              void apiKeys.refetch();
              void endpoints.refetch();
            }}
          />
        </div>
      </div>
    </>
  );
}

export const Route = createFileRoute('/app/')({
  component: Overview,
  validateSearch: validateOverviewSearch,
});
